import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';

import { EventTypes, SignUpSteps } from '@site-mate/dashpivot-shared-library';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { UniqueEmailState } from 'app/shared/directive/unique-email-state.enum';
import { IPasswordValidation } from 'app/shared/model/password-validation.model';
import { RegexpMatchers } from 'app/shared/model/regexp-matches.model';
import { SignUp } from 'app/shared/model/sign-up.model';
import { AppUtilService } from 'app/shared/service/app-util.service';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { ToastrService } from 'app/shared/service/toastr.service';

import { SignUpFormSlideAnimation } from './sign-up-form-slide-animation.model';
import { UsedDomainModalComponent } from './used-domain-modal/used-domain-modal.component';
import { SignUpService } from '../../sign-up.service';

@Component({
  selector: 'cc-sign-up-user-info',
  templateUrl: './sign-up-user-info.component.html',
  styleUrls: ['./sign-up-user-info.component.scss'],
  animations: [SignUpFormSlideAnimation],
})
export class SignUpUserInfoComponent implements OnInit {
  readonly sitemateColor = '#2E81DA';

  @ViewChild('userInfoForm')
  userInfoForm: NgForm;

  @ViewChild('email', { read: ElementRef })
  emailInputElement: ElementRef;

  model: SignUp = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    signature: '',
    mobile: '',
    companyName: '',
    colorCode: this.sitemateColor,
  } as SignUp;

  disabledPasswordToolTip: string;
  isSSODomain: boolean;
  showPassword: boolean;
  domain: string;
  isTemplateReferral: boolean;
  isNonTemplateReferral: boolean;

  passwordValidation: IPasswordValidation;
  combinedPasswordValidationRegex: RegExp;

  unsubscribe = new Subject<void>();

  readonly doNotAcceptWithoutAlphanumeric = RegexpMatchers.doNotAcceptWithoutAlphanumeric;
  public emailRegex: RegExp;

  shouldShowFieldsBelowEmail = false;

  constructor(
    private readonly companyService: CompanyService,
    private readonly appUtilService: AppUtilService,
    private readonly changeDetectionRef: ChangeDetectorRef,
    private readonly errorHandler: ErrorHandler,
    private readonly i18nService: TmpI18NService,
    private readonly signUpService: SignUpService,
    private readonly modal: NgbModal,
    private readonly eventsService: EventsService,
    private readonly toastrService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.setSignupReferralType();
    this.disabledPasswordToolTip = this.i18nService.getMessage('passwordDisabledForSSOSignup');
    this.emailRegex = this.appUtilService.emailRegex;
    this.combinedPasswordValidationRegex = this.signUpService.getCombinedPasswordValidationRegex();
  }

  ngAfterViewInit(): void {
    this.changeDetectionRef.detectChanges();
  }

  handleNextClick(event: any): void {
    let nextAction = 'setFieldsBelowEmailVisible';

    if (!this.shouldShowFieldsBelowEmail) {
      this.verifySSODomain(this.model.email);
      event.preventDefault();

      this.verifyDomainInUse(this.model.email).then((isDomainInUse) => {
        if (isDomainInUse) {
          nextAction = 'showDomainWarningModal';
        }

        this[nextAction]();
      });
    }
  }

  async submitDetails(): Promise<void> {
    this.model.companyName = this.model.companyName.trim();
    const ssoSignupPlaceHolderPassword = 'SSOSignup123';

    if (this.model.companyName && this.model.email) {
      const { success, errorMessage } = await this.signUpService.handleAccountSubdomain(
        this.model.email,
        this.model.companyName,
      );

      if (!success) {
        this.toastrService.error(errorMessage);
        return;
      }
    }

    const isFormValid = this.userInfoForm?.valid || false;
    if (isFormValid) {
      this.signUpService.updateSignupModel({
        ...this.model,
        password: this.isSSODomain ? ssoSignupPlaceHolderPassword : this.model.password,
      });
      this.navigateToBusinessAttributesSelector();
    }
  }

  hasEmailFormattingError(email: NgModel): boolean {
    const isRegexMismatch = email.errors?.email && !email.errors.required;
    return isRegexMismatch || email.errors?.uniqueEmail?.invalidFormat;
  }

  hasFormError(field: NgModel): boolean {
    const requiredValidation =
      this.userInfoForm?.submitted && this.userInfoForm.untouched && !this.userInfoForm.dirty;
    const hasError = (field.invalid && (field.dirty || field.touched)) || requiredValidation;

    if (field.name === 'email' && hasError) {
      this.addInvalidEmailStyling();
    }

    const passwordNotRequiredForSSO = this.isSSODomain && field.name === 'password';
    if (passwordNotRequiredForSSO) {
      return false;
    }

    return hasError;
  }

  onPasswordChange(value: string) {
    this.model.password = value;

    this.passwordValidation = this.signUpService.validatePassword(this.model.password);
  }

  addInvalidEmailStyling() {
    const input = this.emailInputElement?.nativeElement;

    if (input?.classList.contains(UniqueEmailState.Valid)) {
      input.classList.remove(UniqueEmailState.Valid);
      input.classList.add(UniqueEmailState.Invalid);
    }
  }

  private setSignupReferralType(): void {
    this.isTemplateReferral = this.signUpService.isTemplateReferral;
    this.isNonTemplateReferral = this.signUpService.isNonTemplateReferral;
  }

  private navigateToBusinessAttributesSelector() {
    this.signUpService.setActiveStep(SignUpSteps.BusinessAttributesSelector);
    this.eventsService.trackEvent(EventTypes.CtaClicked, { Context: 'Signup Page 1' });
  }

  private verifySSODomain(email: string) {
    this.companyService
      .isSSODomain(email)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        ({ isSSODomain }) => {
          this.isSSODomain = isSSODomain;
          if (this.isSSODomain) {
            this.model.password = '';
          }
          this.signUpService.updateSignupModel({ isSSOSignUp: this.isSSODomain });
          this.changeDetectionRef.detectChanges();
        },
        (error) => this.errorHandler.handle(error),
      );
  }

  private async verifyDomainInUse(email: string): Promise<boolean> {
    const { isBusinessDomainInUse } = await lastValueFrom(this.companyService.isBusinessDomainInUse(email));

    return isBusinessDomainInUse;
  }

  // Next button actions
  private setFieldsBelowEmailVisible(): void {
    this.shouldShowFieldsBelowEmail = true;
    this.changeDetectionRef.detectChanges();
  }

  private showDomainWarningModal() {
    const modalRef = this.modal.open(UsedDomainModalComponent, {
      windowClass: 'modal-600',
      modalDialogClass: 'modal-dialog-centered',
    });
    this.domain = this.model.email.split('@')[1];
    modalRef.componentInstance.domain = this.domain;

    modalRef.result
      .then((modalResult) => {
        if (modalResult.createWorkspace) {
          this.shouldShowFieldsBelowEmail = true;
        } else if (modalResult.joinExistingWorkspace) {
          this.signUpService.setActiveStep(SignUpSteps.JoinExistingWorkspace);
        }
        return true;
      })
      .catch(() => {
        // no-op
      });
  }
}
